"use client";
import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { parseOpenDaysSearchParams } from "@packages/shared-components/sr-page/open-days-api-utils/opendays-url-utils";

export interface OpenDaysFilters {
  studyLevel: string;
  provider: string;
  providerId: string;
  location: string;
  distance: string;
  postcode: string;
  monthYear: string;
  eventType: string;
  pageno: string;
  dateFrom: string;
  dateTo: string;
  isVirtualTour?: boolean;
}

export interface OpenDaysSearchFiltersData {
  totalOpenDaysCount?: number;
  date?: any[];
  university?: any[];
  eventType?: any[];
  location?: {
    country?: any[];
    region?: any[];
    city?: any[];
  };
}

interface OpenDaysFilterContextType {
  filters: OpenDaysFilters;
  setFilters: (filters: OpenDaysFilters) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  appliedFilterCount: number;
  selectedFilter: string;
  setSelectedFilter: (filter: string) => void;
  searchFilters: OpenDaysSearchFiltersData | null;
  setSearchFilters: (data: OpenDaysSearchFiltersData | null) => void;
}

const OpenDaysFilterContext = createContext<OpenDaysFilterContextType | undefined>(undefined);

export const OpenDaysFilterProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getFiltersFromUrl = useCallback((): OpenDaysFilters => {
    const params: Record<string, string | undefined> = {};
    searchParams?.forEach((value, key) => {
      params[key] = value;
    });
    const parsed = parseOpenDaysSearchParams(params);
    return {
      studyLevel: parsed.studyLevel,
      provider: parsed.provider,
      providerId: parsed.providerId,
      location: parsed.location,
      distance: parsed.distance || "any",
      postcode: parsed.postcode || "",
      monthYear: parsed.monthYear,
      eventType: parsed.eventType,
      pageno: parsed.pageno,
      dateFrom: parsed.dateFrom || "",
      dateTo: parsed.dateTo || "",
      isVirtualTour: parsed.isVirtualTour || false,
    };
  }, [searchParams]);

  const [filters, setFilters] = useState<OpenDaysFilters>(getFiltersFromUrl);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [searchFilters, setSearchFilters] = useState<OpenDaysSearchFiltersData | null>(null);

  // Sync filters with URL on navigation
  useEffect(() => {
    setFilters(getFiltersFromUrl());
    setIsLoading(false);
  }, [pathname, searchParams]);

  // Calculate applied filter count
  const appliedFilterCount = [
    filters.studyLevel,
    filters.providerId,
    filters.location,
    filters.postcode,
    filters.distance && filters.distance !== "any" ? filters.distance : "",
    filters.monthYear,
    filters.eventType,
    filters.dateFrom && filters.dateTo ? "date-range" : "",
  ].filter(Boolean).length;

  return (
    <OpenDaysFilterContext.Provider
      value={{
        filters,
        setFilters,
        isFilterOpen,
        setIsFilterOpen,
        isLoading,
        setIsLoading,
        appliedFilterCount,
        selectedFilter,
        setSelectedFilter,
        searchFilters,
        setSearchFilters,
      }}
    >
      {children}
    </OpenDaysFilterContext.Provider>
  );
};

export const useOpenDaysFilterContext = () => {
  const context = useContext(OpenDaysFilterContext);
  if (!context) {
    throw new Error("useOpenDaysFilterContext must be used within OpenDaysFilterProvider");
  }
  return context;
};
